import { useState } from 'react'
import { useBrandColors } from '../../hooks/useBrandColors'
import { useRole, ROLE_LABELS, ROLE_COLORS } from '../../hooks/useRole'

const demoEmployees = [
  { id:1,  name:'Tiffany Gallagher', init:'TG', dept:'Executive',       role:'owner',      email:'tiffany@democompany.co' },
  { id:2,  name:'John Gallagher',    init:'JG', dept:'Executive',       role:'owner',      email:'john@democompany.co' },
  { id:3,  name:'Kim Park',          init:'KP', dept:'Administrative',  role:'hr_admin',   email:'kim@democompany.co' },
  { id:4,  name:'Jamie Mitchell',    init:'JM', dept:'Operations',      role:'employee',   email:'jamie@democompany.co' },
  { id:5,  name:'Sam Rivera',        init:'SR', dept:'Operations',      role:'supervisor', email:'sam@democompany.co' },
  { id:6,  name:'Dana Lee',          init:'DL', dept:'Field Staff',     role:'supervisor', email:'dana@democompany.co' },
  { id:7,  name:'Marcus Thomas',     init:'MT', dept:'Operations',      role:'employee',   email:'marcus@democompany.co' },
  { id:8,  name:'Jordan Collins',    init:'JC', dept:'Field Staff',     role:'employee',   email:'jordan@democompany.co' },
  { id:9,  name:'Paula Reyes',       init:'PR', dept:'Customer Service',role:'employee',   email:'paula@democompany.co' },
  { id:10, name:'Lena Fischer',      init:'LF', dept:'Field Staff',     role:'employee',   email:'lena@democompany.co' },
  { id:11, name:'Marcus Webb',       init:'MW', dept:'Operations',      role:'employee',   email:'mwebb@democompany.co' },
  { id:12, name:'Sofia Reyes',       init:'SR', dept:'Customer Service',role:'employee',   email:'sofia@democompany.co' },
]

const roleDescriptions = {
  owner:      'Full access to all features including billing and client configuration.',
  hr_admin:   'Full HR access: employees, approvals, performance, memos, documents. Cannot access client config or billing.',
  supervisor: 'Can view and manage their department only: approvals, performance, team memos.',
  employee:   'Standard employee portal access only.',
}

export default function RoleManagement() {
  const { accent, accentBg } = useBrandColors()
  const { can, isOwner } = useRole()
  const [employees, setEmployees] = useState(demoEmployees)
  const [toast, setToast] = useState(null)
  const [filterRole, setFilterRole] = useState('All')

  function showToast(msg) { setToast(msg); setTimeout(()=>setToast(null), 3000) }

  function changeRole(id, newRole) {
    setEmployees(es => es.map(e => e.id===id ? {...e, role:newRole} : e))
    const name = employees.find(e=>e.id===id)?.name
    showToast(`${name} updated to ${ROLE_LABELS[newRole]}`)
  }

  const filtered = filterRole==='All' ? employees : employees.filter(e=>e.role===filterRole)
  const roleCounts = Object.fromEntries(['owner','hr_admin','supervisor','employee'].map(r=>[r, employees.filter(e=>e.role===r).length]))

  if (!can.clientConfig && !isOwner) {
    return (
      <div style={{fontFamily:"'Montserrat',sans-serif",textAlign:'center',padding:'60px 20px'}}>
        <div style={{fontSize:48,marginBottom:16}}>🔒</div>
        <div style={{fontFamily:'Georgia,serif',fontSize:'1.3rem',color:'#2B2B2B',marginBottom:8}}>Access Restricted</div>
        <div style={{fontSize:13,color:'#6B7D6B'}}>Role management is only available to Owner accounts.</div>
      </div>
    )
  }

  return (
    <div style={{fontFamily:"'Montserrat',sans-serif"}}>

      {toast && (
        <div style={{position:'fixed',top:20,right:24,zIndex:2000,background:'#2B2B2B',color:'#fff',padding:'12px 18px',borderRadius:8,fontSize:12,fontWeight:500,boxShadow:'0 4px 20px rgba(0,0,0,0.2)',display:'flex',alignItems:'center',gap:10}}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#4A7C59" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#4A7C59" strokeWidth="1.5" strokeLinecap="round"/></svg>
          {toast}
        </div>
      )}

      <div style={{marginBottom:20}}>
        <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.4rem',color:'#2B2B2B',fontWeight:600}}>Role Management</h1>
        <p style={{fontSize:11,color:'#6B7D6B',marginTop:2}}>Control what each team member can see and do in Collective Staff</p>
      </div>

      {/* Role overview cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
        {[
          {role:'owner',     icon:'👑', count:roleCounts.owner},
          {role:'hr_admin',  icon:'🛡️', count:roleCounts.hr_admin},
          {role:'supervisor',icon:'👔', count:roleCounts.supervisor},
          {role:'employee',  icon:'👤', count:roleCounts.employee},
        ].map(({role:r, icon, count})=>(
          <div key={r} style={{background:'#fff',border:`1px solid ${ROLE_COLORS[r]}33`,borderRadius:10,padding:'14px 16px',borderTop:`3px solid ${ROLE_COLORS[r]}`}}>
            <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
            <div style={{fontSize:11,fontWeight:700,color:ROLE_COLORS[r],textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2}}>{ROLE_LABELS[r]}</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:'1.5rem',color:'#2B2B2B',fontWeight:600,marginBottom:4}}>{count}</div>
            <div style={{fontSize:10,color:'#8A9E8A',lineHeight:1.4}}>{roleDescriptions[r]}</div>
          </div>
        ))}
      </div>

      {/* Permission matrix */}
      <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden',marginBottom:16}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
          <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Permission Reference</div>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead>
              <tr style={{background:'#F7F2EB'}}>
                <th style={{textAlign:'left',padding:'8px 14px',fontSize:10,color:'#6B7D6B',fontWeight:600,borderBottom:'1px solid #EDE6DA',minWidth:180}}>Permission</th>
                {['owner','hr_admin','supervisor','employee'].map(r=>(
                  <th key={r} style={{textAlign:'center',padding:'8px 14px',fontSize:10,color:ROLE_COLORS[r],fontWeight:700,borderBottom:'1px solid #EDE6DA',textTransform:'uppercase',letterSpacing:'0.08em'}}>{ROLE_LABELS[r]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['View All Employees',    true,  true,  false, false],
                ['View Department Only',  false, false, true,  false],
                ['Edit Employee Records', true,  true,  false, false],
                ['Approve Timesheets',    true,  true,  true,  false],
                ['Approve Time Off',      true,  true,  true,  false],
                ['View Performance',      true,  true,  true,  false],
                ['Edit Performance',      true,  true,  false, false],
                ['Send Memos',            true,  true,  false, false],
                ['Manage Documents',      true,  true,  false, false],
                ['View Helpdesk',         true,  true,  false, false],
                ['Visitor Management',    true,  true,  false, false],
                ['Team Calendar Edit',    true,  true,  false, false],
                ['Dev Requests',          true,  true,  false, false],
                ['Client Config',         true,  false, false, false],
              ].map(([label, ...perms], i)=>(
                <tr key={label} style={{borderBottom:'1px solid #EDE6DA',background:i%2===0?'#fff':'#FAFAF8'}}>
                  <td style={{padding:'7px 14px',color:'#4A4A4A',fontWeight:500}}>{label}</td>
                  {perms.map((has, j)=>(
                    <td key={j} style={{padding:'7px 14px',textAlign:'center'}}>
                      {has
                        ? <span style={{color:'#4A7C59',fontSize:14,fontWeight:700}}>✓</span>
                        : <span style={{color:'#EDE6DA',fontSize:14}}>—</span>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee role assignment */}
      <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Employee Role Assignments</div>
          <div style={{display:'flex',gap:6}}>
            {['All','owner','hr_admin','supervisor','employee'].map(f=>(
              <button key={f} onClick={()=>setFilterRole(f)}
                style={{padding:'4px 12px',border:`1px solid ${filterRole===f?(ROLE_COLORS[f]||accent):'#EDE6DA'}`,borderRadius:20,background:filterRole===f?(ROLE_COLORS[f]||accent):'#fff',color:filterRole===f?'#fff':'#4A4A4A',fontSize:10,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                {f==='All'?'All':ROLE_LABELS[f]}
              </button>
            ))}
          </div>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'#F7F2EB'}}>
              {['Employee','Department','Email','Current Role','Change Role'].map(h=>(
                <th key={h} style={{textAlign:'left',padding:'8px 16px',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,borderBottom:'1px solid #EDE6DA'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(e=>(
              <tr key={e.id} style={{borderBottom:'1px solid #EDE6DA'}}
                onMouseEnter={ev=>ev.currentTarget.style.background='rgba(184,115,74,0.02)'}
                onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                <td style={{padding:'11px 16px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <div style={{width:28,height:28,borderRadius:'50%',background:ROLE_COLORS[e.role]+'22',color:ROLE_COLORS[e.role],display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,flexShrink:0}}>{e.init}</div>
                    <span style={{fontWeight:600,color:'#2B2B2B'}}>{e.name}</span>
                  </div>
                </td>
                <td style={{padding:'11px 16px',color:'#6B7D6B'}}>{e.dept}</td>
                <td style={{padding:'11px 16px',color:'#8A9E8A',fontSize:11}}>{e.email}</td>
                <td style={{padding:'11px 16px'}}>
                  <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,padding:'3px 10px',borderRadius:20,fontWeight:600,background:ROLE_COLORS[e.role]+'15',color:ROLE_COLORS[e.role]}}>
                    <span style={{width:6,height:6,borderRadius:'50%',background:ROLE_COLORS[e.role]}}></span>
                    {ROLE_LABELS[e.role]}
                  </span>
                </td>
                <td style={{padding:'11px 16px'}}>
                  <select value={e.role} onChange={ev=>changeRole(e.id, ev.target.value)}
                    style={{padding:'5px 10px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:11,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B',cursor:'pointer'}}>
                    {['owner','hr_admin','supervisor','employee'].map(r=>(
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
